import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { Card, CardContent, Typography, Link, Alert } from '@mui/material';
import { formatBytes } from '../../utils/formatBytes';
import { resolveTicketingApiUrl } from '../../utils/ticketingApiUrl';

function getAuthToken() {
  return Cookies.get('accessToken') || Cookies.get('jwtToken');
}

export default function AttachmentList({ ticketId, attachments = [] }) {
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  if (!attachments.length) return null;

  const handleDownload = async (attachment) => {
    const url = resolveTicketingApiUrl(
      attachment.downloadUrl || `/v1/tickets/${ticketId}/attachments/${attachment.attachmentId}/download`
    );
    const token = getAuthToken();
    setError(null);
    setDownloadingId(attachment.attachmentId);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let message = 'Download failed';
        try {
          const json = JSON.parse(text);
          message = json.message || json.error || message;
        } catch {
          if (text) message = text;
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = attachment.filename || 'attachment';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err?.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Attachments
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {attachments.map((a) => (
            <li key={a.attachmentId} style={{ marginBottom: 4 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => handleDownload(a)}
                disabled={downloadingId === a.attachmentId}
                sx={{ textAlign: 'left', verticalAlign: 'baseline' }}
                aria-label={`Download ${a.filename}`}
              >
                {downloadingId === a.attachmentId ? 'Downloading…' : a.filename}
              </Link>
              {a.sizeBytes != null && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({formatBytes(a.sizeBytes)})
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
